import { useState } from 'react'
import {
  CardNumberElement,
  useElements,
  useStripe
} from '@stripe/react-stripe-js'
import toast from 'react-hot-toast'
import { useRouter } from 'next/router'
import { useSpring } from 'framer-motion'
import { Formik, Form, FormikErrors } from 'formik'

import { Cart } from './Cart'
import { Sharing } from './Sharing'
import { useUser } from '@hooks/user'
import { Address } from '@models/address'
import { UserAccount } from './UserAccount'
import { PaymentInfo } from './PaymentInfo'
import { createClient } from '@services/api'
import { AffiliateComments } from './AffiliateComments'
import { PropertyAddress } from './PropertyAddress'
import { UploadInspection } from './UploadInspection'
import { ViewingRole, SignupDetails } from '@models/user'
import { LoginDialog } from '@components/LoginDialog'

export interface FormValues {
  inspection: File | null
  inspectionKey: string
  property: {
    comments: string
    address: Address | null
  }
  signup: SignupDetails
  sharingTargets: Array<{
    email: string
    role: ViewingRole
  }>
  integrationConsent: boolean
  billing: Address & {
    cardNumber: string
    expiry: string
    cvc: string
    promoCode: string
  }
}

export const initialFormValues: FormValues = {
  inspection: null,
  inspectionKey: '',
  property: {
    address: null,
    comments: ''
  },
  signup: {
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    acceptTOC: false,
    acceptAffiliateTOC: false,
    type: ''
  },
  integrationConsent: true,
  sharingTargets: [{ email: '', role: 'OWNER' }],
  billing: {
    street: '',
    city: '',
    state: '',
    zipCode: '',
    cardNumber: '',
    expiry: '',
    cvc: '',
    promoCode: ''
  }
}

export function CheckoutForm() {
  const { query, push, prefetch } = useRouter()
  const { isLoggedIn, user, register } = useUser()
  const [showPaymentDetails, setShowPaymentDetails] = useState(true)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const progressAmount = useSpring(0)
  const [progressMessage, setProgressMessage] = useState('Validating...')
  const stripe = useStripe()
  const elements = useElements()
  const hasIntegration = Boolean(query.ref && query.inspection)

  function validate(values: FormValues) {
    const errors: FormikErrors<FormValues> = {}

    if (
      !values.inspection &&
      !values.inspectionKey &&
      (!(query.ref && query.inspection) || !values.integrationConsent)
    ) {
      errors.inspection = 'Please upload an inspection PDF'
    }

    if (
      values.inspection &&
      !values.inspectionKey &&
      !(query.ref && query.inspection)
    ) {
      errors.inspection = 'Please wait for upload to finish'
    }

    if (!isLoggedIn) {
      if (values.signup.password !== values.signup.confirmPassword) {
        errors.signup ??= {}
        errors.signup.confirmPassword =
          'Your password confirmation must match your password'
      }

      if (!values.signup.type) {
        errors.signup ??= {}
        errors.signup.type = 'Please select the most relevant profession'
      }

      if (!values.signup.acceptTOC) {
        errors.signup ??= {}
        errors.signup.acceptTOC = 'Please accept the terms and conditions'
      }
    }

    if (!values.property.address && !(query.ref && query.inspection)) {
      errors.property ??= {}
      errors.property.address = 'Please provide a property address'
    }

    return errors
  }

  async function handleSubmit(values: FormValues) {
    const canUseIntegration = Boolean(
      hasIntegration && values.integrationConsent
    )

    if (!isLoggedIn) {
      setProgressMessage('Registering a new user...')

      try {
        await register.mutateAsync({
          firstName: values.signup.firstName,
          lastName: values.signup.lastName,
          email: values.signup.email,
          phoneNumber: values.signup.phoneNumber,
          password: values.signup.password,
          type: values.signup.type
        })
      } catch (error) {
        if (error.response.status === 409) {
          toast.error(
            (error.response.data.message as string) +
              '. Try logging in with the same credentials'
          )
        } else {
          toast.error(error.response.data.message)
        }
        return
      }
    }

    progressAmount.set(30)
    setProgressMessage('Processing billing information...')

    if (!stripe || !elements) {
      return
    }

    let paymentMethodId: string | undefined
    const card = elements.getElement(CardNumberElement)

    if (card) {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        card,
        type: 'card',
        billing_details: {
          name:
            user.data?.name ??
            `${values.signup.firstName} ${values.signup.lastName}`,
          email: user.data?.email ?? values.signup.email,
          address: {
            line1: values.billing.street.toUpperCase(),
            city: values.billing.city.toUpperCase(),
            state: values.billing.state.toUpperCase(),
            postal_code: values.billing.zipCode
          }
        }
      })

      if (error) {
        toast.error(
          `There was a problem processing your payment information: ${error.message}`
        )
        return
      }

      if (paymentMethod) {
        paymentMethodId = paymentMethod.id
      }
    }

    progressAmount.set(75)
    setProgressMessage('Completing your order...')

    try {
      await createClient().post('/checkout', {
        partner: query.ref ?? undefined,
        affiliate: query.affiliate ?? undefined,
        inspectionId: canUseIntegration ? query.inspection : undefined,
        address: values.property.address
          ? {
              street: values.property.address.street,
              city: values.property.address.city,
              state: values.property.address.state,
              zipCode: values.property.address.zipCode
            }
          : undefined,
        propertyComments: values.property.comments ?? undefined,
        paymentMethod: paymentMethodId,
        inspection: values.inspectionKey,
        promoCode: values.billing.promoCode,
        sharingTargets: values.sharingTargets.filter(
          target =>
            Boolean(target.email) &&
            target.email !== (values.signup.email || user.data?.email)
        )
      })

      progressAmount.set(100)
      setProgressMessage('Order complete')

      await prefetch('/thank-you/order')
      await push('/thank-you/order')
    } catch (error) {
      toast.error(
        `There was a problem completing your purchase: ${
          error.response.data.message || error.message
        }`
      )
    } finally {
      progressAmount.set(0)
      setProgressMessage('Processing...')
    }
  }

  function open() {
    setIsDialogOpen(true)
  }

  function close() {
    setIsDialogOpen(false)
  }

  return (
    <>
      <LoginDialog isOpen={isDialogOpen} handleClose={close} />
      <Formik
        initialValues={initialFormValues}
        validate={validate}
        onSubmit={handleSubmit}
      >
        {formik => (
          <div className="container py-8 mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8 lg:pb-48 lg:pt-0">
            <Form className="flex flex-col lg:col-span-2 space-y-8 lg:pt-8">
              {query.affiliate && (
                <AffiliateComments
                  partner={query.affiliate as string}
                  hasIntegration={hasIntegration}
                />
              )}
              {(!hasIntegration || !formik.values.integrationConsent) && (
                <>
                  <UploadInspection />
                  <PropertyAddress />
                </>
              )}
              <UserAccount openLoginModal={open} />
              <Sharing />
              {showPaymentDetails && <PaymentInfo />}
            </Form>
            <aside>
              <Cart
                isSubmitting={formik.isSubmitting}
                progress={progressAmount}
                progressMessage={progressMessage}
                submitOrder={formik.submitForm}
                setPromoCode={(code: string) =>
                  formik.setFieldValue('billing.promoCode', code)
                }
                showPayment={() => setShowPaymentDetails(true)}
                hidePayment={() => setShowPaymentDetails(false)}
              />
            </aside>
          </div>
        )}
      </Formik>
    </>
  )
}
